import { useEffect } from 'react';
import { isArray, kebabCase, keyBy } from 'lodash-es';

import { useAvailableLabels } from '@hotjar/labels';
import type { DateRange } from '@hotjar/webapp/constants/DateFilterRanges';
import type { SegmentTrackData } from '@hotjar/webapp/domains/common/tracking/segment';
import { track } from '@hotjar/webapp/domains/common/tracking/segment';
import { noop } from '@hotjar/webapp/lib/function';

import { useFilterState } from '../use-filter-state';

/**
 * Generate a string representation of a DateRange instance
 * @param range
 */
function dateString(range?: DateRange): string {
  if (!range) return '';

  const { type } = range;

  if (type === 'relative') {
    return `last_${range.amount}_${range.unit}`;
  }

  if (type === 'absolute') {
    return [range.from.toISOString(), range.to.toISOString()].join(',');
  }

  return 'all';
}

/**
 * Generate an array representation of a local filter values
 * @param obj
 */
function filterToArray(obj: { [key: string]: string | number | string[] | number[] }): string[] {
  return Object.entries(obj).map(([key, val]) => {
    return isArray(val) ? `${key}:${val.join(',')}` : `${key}:${val}`;
  });
}

const TRACK_EVENT = 'Local Filters Applied';

export interface UseTrackHighlightFiltersEffectProps {
  organizationId: string;
  accountId: string;
  siteId: string;
  collectionName: string;
}

export function useTrackHighlightFiltersEffect({
  organizationId,
  accountId,
  siteId,
  collectionName,
}: UseTrackHighlightFiltersEffectProps): void {
  const [filterState] = useFilterState(noop);
  const { data: availableLabels } = useAvailableLabels(siteId, 'all');

  useEffect(() => {
    if (
      !organizationId ||
      !accountId ||
      !siteId ||
      !collectionName ||
      !filterState ||
      !availableLabels
    ) {
      return;
    }

    const labelsMap = keyBy(availableLabels, (label) => label.id);

    const payload: Record<string, string | string[] | number | number[]> = {
      authors: filterState.authors?.map((author) => author.id),
      sources: filterState.sources,
      labels: filterState.labels
        ? [...filterState.labels].map((label) => kebabCase(labelsMap[label]?.name))
        : [],
      date: dateString(filterState.dateRange),
    };

    payload.status = filterState.status;

    const trackData: SegmentTrackData = {
      account_id: accountId,
      organization_id: organizationId,
      site_id: siteId,
      tool: 'Highlights',
      filters_included: filterToArray(payload),
      state_event_was_triggered_on: `highlights-${
        collectionName ? kebabCase(collectionName) : 'all'
      }`,
      url_event_was_triggered_on: window.location.href,
    };

    track(TRACK_EVENT, trackData);
  }, [organizationId, accountId, siteId, collectionName, filterState, availableLabels]);
}
